import type { AnyRouter } from '@tanstack/react-router'
import type { SentryImpersonation, SentryUser } from './sentry'

/**
 * Lazy facade over `./sentry` so the Sentry SDK (~90KB gz, including the
 * session-replay recorder) never loads on the marketing surface. Callers
 * import `setSentryUser` / `reportError` from here instead of `./sentry`;
 * both buffer until the real module is loaded and initialised, then flush.
 *
 * Lazy chunks that import `./sentry` directly (e.g. the task editor) are
 * fine: the module's own `initialized` flag makes its helpers no-op until
 * `initSentry` has run, and module identity is shared with this facade's
 * dynamic import.
 */
type SentryModule = typeof import('./sentry')

let mod: SentryModule | null = null
let loadStarted = false
/** `undefined` = never set; `null` = explicitly cleared. */
let pendingUser: SentryUser | null | undefined
let pendingImpersonation: SentryImpersonation | null | undefined
const MAX_BUFFERED_ERRORS = 20
const pendingErrors: Parameters<SentryModule['reportError']>[] = []

const load = async (router: AnyRouter) => {
  if (loadStarted) {
    return
  }
  loadStarted = true
  const loaded = await import('./sentry')
  loaded.initSentry(router)
  mod = loaded
  if (pendingUser !== undefined) {
    loaded.setSentryUser(pendingUser)
  }
  if (pendingImpersonation !== undefined) {
    loaded.setImpersonationContext(pendingImpersonation)
  }
  for (const args of pendingErrors.splice(0)) {
    loaded.reportError(...args)
  }
}

export const setSentryUser = (user: SentryUser | null) => {
  if (mod) {
    mod.setSentryUser(user)
    return
  }
  pendingUser = user
}

export const setImpersonationContext = (session: SentryImpersonation | null) => {
  if (mod) {
    mod.setImpersonationContext(session)
    return
  }
  pendingImpersonation = session
}

export const reportError: SentryModule['reportError'] = (error, options) => {
  if (mod) {
    mod.reportError(error, options)
    return
  }
  if (pendingErrors.length < MAX_BUFFERED_ERRORS) {
    pendingErrors.push([error, options])
  }
}

/**
 * Load and initialise Sentry without bundling the SDK into the entry.
 * Called only by the full app router (`src/app-router.tsx`) — the
 * marketing router never constructs Sentry at all, and navigation from
 * marketing into the app is a full-page load that boots the app router.
 */
export const initSentryLazily = (router: AnyRouter) => {
  // Mirrors the gates inside `initSentry`, but checked BEFORE the dynamic
  // import so dev / DSN-less builds never fetch the chunk at all.
  if (typeof window === 'undefined' || import.meta.env.DEV || !import.meta.env.VITE_SENTRY_DSN) {
    return
  }
  void load(router)
}
